/* ==========================================================================
   SMA — Ingredient box carousel
   --------------------------------------------------------------------------
   Drives the `sma_ingredient` block in main-product.liquid when the product
   metafield custom.key_ingredients holds more than one entry.

   The track is a native scroll-snap container, so swipe, trackpad and keyboard
   scrolling all work with no JS at all. This file only adds the three things
   the browser will not do on its own: pagination dots, auto-advance, and
   keeping the two in sync with wherever the user has scrolled to.

   Index is derived from scrollLeft / clientWidth, which holds because every
   slide is exactly 100% of the track width (see sma-product-info.css).
   ========================================================================== */

(function () {
  'use strict';

  var READY_FLAG = 'smaIngredientReady';
  var RESUME_DELAY = 1200;

  function prefersReducedMotion() {
    return (
      window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches
    );
  }

  function initCarousel(root) {
    if (!root || root.dataset[READY_FLAG] === '1') return;

    var track = root.querySelector('[data-sma-ingredient-track]');
    if (!track) return;

    var slides = Array.prototype.slice.call(track.children);
    root.dataset[READY_FLAG] = '1';
    if (slides.length < 2) return;

    var dots = Array.prototype.slice.call(root.querySelectorAll('[data-sma-ingredient-dot]'));
    var delay = parseInt(root.dataset.smaIngredientAutoplay, 10);
    if (isNaN(delay) || delay < 1000) delay = 0; /* 0 = auto-advance off */

    var index = 0;
    var timer = null;
    var resumeTimer = null;
    var held = false; /* pointer down / hover / focus inside */
    var visible = true;
    var scrollSettle = null;

    /* ---- position -------------------------------------------------------- */

    function paint() {
      for (var i = 0; i < dots.length; i++) {
        dots[i].classList.toggle('is-active', i === index);
        dots[i].setAttribute('aria-current', i === index ? 'true' : 'false');
      }
    }

    function goTo(next, animate) {
      var total = slides.length;
      index = ((next % total) + total) % total;
      var left = index * track.clientWidth;
      if (animate && !prefersReducedMotion() && typeof track.scrollTo === 'function') {
        track.scrollTo({ left: left, behavior: 'smooth' });
      } else {
        track.scrollLeft = left;
      }
      paint();
    }

    /* ---- autoplay -------------------------------------------------------- */

    function stop() {
      if (timer) {
        clearInterval(timer);
        timer = null;
      }
    }

    function start() {
      stop();
      if (!delay || held || !visible || document.hidden) return;
      timer = setInterval(function () {
        goTo(index + 1, true);
      }, delay);
    }

    function hold() {
      held = true;
      if (resumeTimer) clearTimeout(resumeTimer);
      stop();
    }

    function release(immediate) {
      if (resumeTimer) clearTimeout(resumeTimer);
      resumeTimer = setTimeout(
        function () {
          held = false;
          start();
        },
        immediate ? 0 : RESUME_DELAY
      );
    }

    /* ---- events ---------------------------------------------------------- */

    dots.forEach(function (dot) {
      dot.addEventListener('click', function () {
        hold();
        goTo(parseInt(dot.dataset.smaIngredientDot, 10) || 0, true);
        release();
      });
    });

    /* Follow the user when they swipe or scroll the track themselves. The
       settle timeout is what tells a finished swipe apart from the frames of a
       smooth programmatic scroll. */
    track.addEventListener(
      'scroll',
      function () {
        if (scrollSettle) clearTimeout(scrollSettle);
        scrollSettle = setTimeout(function () {
          var width = track.clientWidth;
          if (!width) return;
          var next = Math.round(track.scrollLeft / width);
          if (next !== index && next >= 0 && next < slides.length) {
            index = next;
            paint();
          }
        }, 90);
      },
      { passive: true }
    );

    root.addEventListener('mouseenter', hold);
    root.addEventListener('mouseleave', function () {
      release(true);
    });
    root.addEventListener('focusin', hold);
    root.addEventListener('focusout', function () {
      release(true);
    });
    track.addEventListener('touchstart', hold, { passive: true });
    track.addEventListener('touchend', function () {
      release();
    });
    track.addEventListener('touchcancel', function () {
      release();
    });

    document.addEventListener('visibilitychange', function () {
      if (document.hidden) stop();
      else start();
    });

    /* Slide width changes with the viewport, so realign rather than leaving the
       track parked between two slides. */
    var resizeTimer = null;
    window.addEventListener('resize', function () {
      if (resizeTimer) clearTimeout(resizeTimer);
      resizeTimer = setTimeout(function () {
        goTo(index, false);
      }, 150);
    });

    if ('IntersectionObserver' in window) {
      new IntersectionObserver(
        function (entries) {
          visible = entries[0].isIntersecting;
          if (visible) start();
          else stop();
        },
        { threshold: 0.25 }
      ).observe(root);
    }

    paint();
    start();
  }

  function initAll(scope) {
    var context = scope && scope.querySelectorAll ? scope : document;
    var roots = context.querySelectorAll('[data-sma-ingredient-carousel]');
    Array.prototype.forEach.call(roots, initCarousel);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      initAll(document);
    });
  } else {
    initAll(document);
  }

  /* Theme editor: the section is re-rendered wholesale on every setting change. */
  document.addEventListener('shopify:section:load', function (event) {
    initAll(event.target || document);
  });

  /* Variant changes swap parts of the info column through HTMLUpdateUtility. */
  document.addEventListener('sma:product:updated', function () {
    initAll(document);
  });
})();
